/**
 * Mandate validator: a line-by-line TypeScript mirror of `validate` and
 * `validate_json` in `program/src/mandate.rs` (the source of truth).
 *
 * Why a second copy exists: the form must tell the user what is wrong while they
 * type, without a round trip. To stop the two copies drifting apart, the Rust
 * side generated GOLDEN VECTORS (`__fixtures__/mandate_vectors.json`) and
 * `__tests__/validate.golden.test.ts` asserts that this file returns exactly the
 * same set of violating fields for every vector. Change a rule in Rust and the
 * vectors change; this file must follow. The server is always the final judge;
 * this validator is advisory UI feedback and never a substitute for it.
 *
 * Conventions kept from the Rust module: ratios are fractions (10% = 0.10, and a
 * bare 10 is rejected with a hint), money is a decimal string plus a currency,
 * unknown fields are rejected, and anything not listed in `autonomy.may` is not
 * permitted.
 */

import type {
  AgentAction,
  AutonomyLevel,
  MandateBody,
  Money,
  Violation,
} from './types';

export const REQUIRED_ACK_DOCS = ['own_capital_attestation', 'risk_disclosure'] as const;

/** Actions that can never be granted to the agent; always present in `never`. */
export const REQUIRED_NEVER: AgentAction[] = [
  'change_limits',
  'resume_after_halt',
  'change_credentials',
  'withdraw',
];

/** Actions that move or size positions; these need autonomy level L2 or above. */
const ORDER_ACTIONS: AgentAction[] = [
  'place_orders',
  'cancel_orders',
  'resize_within_limits',
  'rebalance',
  'flatten',
];

const ALL_ACTIONS: readonly string[] = [
  'place_orders',
  'cancel_orders',
  'resize_within_limits',
  'rebalance',
  'halt',
  'flatten',
  'add_strategy',
  'remove_strategy',
  'raise_target_risk',
  'change_limits',
  'resume_after_halt',
  'change_credentials',
  'withdraw',
];

const LEVELS: readonly string[] = ['L0', 'L1', 'L2', 'L3'];

const LEVEL_RANK: Record<AutonomyLevel, number> = { L0: 0, L1: 1, L2: 2, L3: 3 };

// ---------------------------------------------------------------------------
// Decimal (mirrors what rust_decimal::Decimal::from_str accepts)
// ---------------------------------------------------------------------------

/** A decimal as an integer mantissa and a non-negative count of decimal places. */
export interface Dec {
  mant: bigint;
  scale: number;
}

const DEC_MAX_MANT = 2n ** 96n; // rust_decimal keeps a 96-bit mantissa
const DEC_MAX_SCALE = 28;
const DEC_RE = /^([+-]?)(\d[\d_]*)?(?:\.([\d_]*))?(?:[eE]([+-]?\d+))?$/;

/**
 * Parse a decimal string the way `Decimal::from_str` does for the inputs a
 * mandate can plausibly carry: optional sign, digits (underscores are ignored),
 * an optional fraction, an optional exponent. Returns null when the text is not a
 * decimal or does not fit in 96 bits. Extra places beyond 28 are rounded away
 * (half to even), so a positive value below 1e-28 becomes zero.
 */
export function parseDecimal(input: string): Dec | null {
  const s = input.trim();
  const m = DEC_RE.exec(s);
  if (!m) return null;
  const [, sign, intPart = '', fracPart = '', exp] = m;
  const intDigits = intPart.replace(/_/g, '');
  const fracDigits = fracPart.replace(/_/g, '');
  if (intDigits.length + fracDigits.length === 0) return null;
  let mant = BigInt(`${intDigits}${fracDigits}` || '0');
  let scale = fracDigits.length - (exp ? Number(exp) : 0);
  if (!Number.isFinite(scale) || Math.abs(scale) > 400) return null;
  if (scale < 0) {
    mant *= 10n ** BigInt(-scale);
    scale = 0;
  }
  // Drop decimal places (rounding half to even) while there are more than 28 or
  // the mantissa does not fit in 96 bits, as rust_decimal does; whole numbers
  // that still do not fit are rejected.
  while (scale > 0 && (scale > DEC_MAX_SCALE || mant >= DEC_MAX_MANT)) {
    const q = mant / 10n;
    const r = mant % 10n;
    mant = r > 5n || (r === 5n && q % 2n === 1n) ? q + 1n : q;
    scale -= 1;
  }
  if (mant >= DEC_MAX_MANT) return null;
  return { mant: sign === '-' ? -mant : mant, scale };
}

function alignTo(d: Dec, scale: number): bigint {
  return d.mant * 10n ** BigInt(scale - d.scale);
}

/** -1, 0 or 1 as `a` is less than, equal to or greater than `b`. */
export function compareDec(a: Dec, b: Dec): number {
  const scale = Math.max(a.scale, b.scale);
  const x = alignTo(a, scale);
  const y = alignTo(b, scale);
  return x < y ? -1 : x > y ? 1 : 0;
}

const DEC_ZERO: Dec = { mant: 0n, scale: 0 };

/** Convert to a JS number for display only (never for a comparison that matters). */
export function decToNumber(d: Dec): number {
  return Number(d.mant) / 10 ** d.scale;
}

// ---------------------------------------------------------------------------
// Rule helpers (same names and messages as the Rust module)
// ---------------------------------------------------------------------------

function fraction(field: string, v: number, out: Violation[]): void {
  if (!Number.isFinite(v) || v <= 0) {
    out.push({ field, message: 'must be greater than 0 (a fraction such as 0.10 for 10%)' });
  } else if (v > 1) {
    out.push({
      field,
      message: `${v} is above 1: ratios are fractions, so 10% is written 0.10, not 10`,
    });
  }
}

function multiple(field: string, v: number, out: Violation[]): boolean {
  if (!Number.isFinite(v) || v <= 0) {
    out.push({ field, message: 'must be greater than 0' });
    return false;
  }
  return true;
}

function money(field: string, m: Money, out: Violation[]): Dec | null {
  let ok = true;
  if (m.ccy.length !== 3 || !/^[A-Z]{3}$/.test(m.ccy)) {
    out.push({ field: `${field}.ccy`, message: 'must be a 3-letter uppercase currency code such as USD' });
    ok = false;
  }
  const d = parseDecimal(m.amount);
  if (d === null) {
    out.push({
      field: `${field}.amount`,
      message: 'must be a decimal number written as a string, e.g. "5000.00"',
    });
    return null;
  }
  if (compareDec(d, DEC_ZERO) > 0) return ok ? d : null;
  out.push({ field: `${field}.amount`, message: 'must be greater than 0' });
  return null;
}

function nonemptyList(field: string, items: string[], out: Violation[]): void {
  if (items.length === 0) {
    out.push({ field, message: 'must list at least one entry' });
  }
  if (items.some((s) => s.trim() === '')) {
    out.push({ field, message: 'entries must not be blank' });
  }
}

// ---------------------------------------------------------------------------
// validate: every invariant, ALL violations returned (empty = valid)
// ---------------------------------------------------------------------------

/**
 * Check every invariant and return ALL violations (empty = valid), so a user
 * fixing a form sees everything at once rather than one error at a time.
 */
export function validateMandate(m: MandateBody): Violation[] {
  const v: Violation[] = [];

  // --- basis
  if (m.basis.capital_source !== 'own') {
    v.push({
      field: 'basis.capital_source',
      message:
        'only your own capital is supported right now; third-party or pooled money is not available',
    });
  }
  const j = m.basis.jurisdiction.trim();
  if (j === '' || j.length > 16 || !/^[A-Za-z0-9-]+$/.test(j)) {
    v.push({
      field: 'basis.jurisdiction',
      message: 'must be a country or country-region code such as US-GA',
    });
  }
  for (const doc of REQUIRED_ACK_DOCS) {
    if (!m.basis.acknowledgements.some((a) => a.doc === doc)) {
      v.push({
        field: 'basis.acknowledgements',
        message: `missing required acknowledgement: ${doc}`,
      });
    }
  }
  m.basis.acknowledgements.forEach((a, i) => {
    if (a.by.trim() === '' || a.doc_version.trim() === '') {
      v.push({
        field: `basis.acknowledgements[${i}]`,
        message: 'needs a signer and a document version',
      });
    }
  });

  // --- capital
  const allocated = money('capital.allocated', m.capital.allocated, v);
  const reserve = m.capital.min_cash_reserve;
  if (!Number.isFinite(reserve) || !(reserve >= 0 && reserve < 1)) {
    v.push({
      field: 'capital.min_cash_reserve',
      message: 'must be a fraction from 0 up to (not including) 1, e.g. 0.05 for 5%',
    });
  }

  // --- universe
  nonemptyList('universe.venues', m.universe.venues, v);
  nonemptyList('universe.asset_classes', m.universe.asset_classes, v);
  nonemptyList('universe.instrument_allow', m.universe.instrument_allow, v);
  const allow = new Set(m.universe.instrument_allow.map((s) => s.trim().toUpperCase()));
  const overlap = m.universe.instrument_deny.filter((s) => allow.has(s.trim().toUpperCase()));
  if (overlap.length > 0) {
    v.push({
      field: 'universe.instrument_deny',
      message: `also listed in instrument_allow: ${JSON.stringify(overlap)}`,
    });
  }
  let leverageOk = true;
  if (!Number.isFinite(m.universe.leverage_max_gross) || m.universe.leverage_max_gross < 1) {
    v.push({
      field: 'universe.leverage_max_gross',
      message: 'must be at least 1 (1 means no leverage)',
    });
    leverageOk = false;
  }

  // --- exposure (multiples of equity, bounded by the leverage cap)
  const e = m.exposure;
  const grossOk = multiple('exposure.max_gross', e.max_gross, v);
  const netOk = multiple('exposure.max_net', e.max_net, v);
  const posOk = multiple('exposure.max_position', e.max_position, v);
  if (grossOk && leverageOk && e.max_gross > m.universe.leverage_max_gross) {
    v.push({ field: 'exposure.max_gross', message: 'cannot exceed universe.leverage_max_gross' });
  }
  if (grossOk && netOk && e.max_net > e.max_gross) {
    v.push({ field: 'exposure.max_net', message: 'cannot exceed exposure.max_gross' });
  }
  if (grossOk && posOk && e.max_position > e.max_gross) {
    v.push({
      field: 'exposure.max_position',
      message: `${e.max_position} is above the gross limit ${e.max_gross}: if you meant a percent, use a fraction (0.25 for 25%)`,
    });
  }
  for (const [cls, cap] of Object.entries(e.max_asset_class).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))) {
    const f = `exposure.max_asset_class.${cls}`;
    if (!m.universe.asset_classes.includes(cls)) {
      v.push({ field: f, message: 'is not one of universe.asset_classes' });
    }
    if (multiple(f, cap, v) && grossOk && cap > e.max_gross) {
      v.push({ field: f, message: 'cannot exceed exposure.max_gross' });
    }
  }
  // Both sides are evaluated before the check, as in the Rust tuple pattern.
  const notional = money('exposure.max_order_notional', e.max_order_notional, v);
  if (allocated !== null && notional !== null) {
    if (e.max_order_notional.ccy !== m.capital.allocated.ccy) {
      v.push({ field: 'exposure.max_order_notional.ccy', message: 'must match capital.allocated.ccy' });
    } else if (compareDec(notional, allocated) > 0) {
      v.push({
        field: 'exposure.max_order_notional',
        message: 'cannot exceed the allocated capital',
      });
    }
  }
  if (e.max_orders_per_day === 0) {
    v.push({ field: 'exposure.max_orders_per_day', message: 'must be at least 1' });
  }
  multiple('exposure.max_turnover_per_day', e.max_turnover_per_day, v);

  // --- loss limits
  fraction('loss.daily_loss_limit', m.loss.daily_loss_limit, v);
  const ladder = m.loss.drawdown_ladder;
  if (ladder.length === 0) {
    v.push({
      field: 'loss.drawdown_ladder',
      message: 'needs at least one rung, ending in halt_flatten',
    });
  }
  let prevAt = 0;
  let prevScale = 1;
  ladder.forEach((rung, i) => {
    const f = `loss.drawdown_ladder[${i}]`;
    fraction(`${f}.at`, rung.at, v);
    if (Number.isFinite(rung.at) && rung.at <= prevAt) {
      v.push({ field: `${f}.at`, message: 'rungs must be in strictly increasing order' });
    }
    prevAt = rung.at;
    const last = i + 1 === ladder.length;
    const scale = rung.scale ?? null;
    if (rung.action === 'shrink') {
      if (scale !== null && Number.isFinite(scale) && scale > 0 && scale < 1) {
        if (scale > prevScale) {
          v.push({ field: `${f}.scale`, message: 'a later rung cannot scale positions back up' });
        }
        prevScale = scale;
      } else {
        v.push({
          field: `${f}.scale`,
          message: 'a shrink rung needs a scale strictly between 0 and 1',
        });
      }
    } else if (scale !== null) {
      v.push({ field: `${f}.scale`, message: 'a halt_flatten rung takes no scale' });
    }
    if (last && rung.action !== 'halt_flatten') {
      v.push({ field: f, message: 'the last rung must be halt_flatten' });
    }
    if (!last && rung.action === 'halt_flatten') {
      v.push({
        field: f,
        message: 'halt_flatten must be the last rung; later rungs could never be reached',
      });
    }
  });
  const lastRung = ladder[ladder.length - 1];
  if (lastRung !== undefined) {
    if (
      Number.isFinite(m.loss.daily_loss_limit) &&
      Number.isFinite(lastRung.at) &&
      m.loss.daily_loss_limit > lastRung.at
    ) {
      v.push({
        field: 'loss.daily_loss_limit',
        message: 'cannot be larger than the final drawdown rung',
      });
    }
  }

  // --- autonomy
  const a = m.autonomy;
  for (const required of REQUIRED_NEVER) {
    if (!a.never.includes(required)) {
      v.push({ field: 'autonomy.never', message: `must always include ${required}` });
    }
  }
  const seen = new Map<AgentAction, string>();
  const lists: Array<[AgentAction[], string]> = [
    [a.may, 'may'],
    [a.needs_confirmation, 'needs_confirmation'],
    [a.never, 'never'],
  ];
  for (const [list, name] of lists) {
    for (const action of list) {
      const prev = seen.get(action);
      seen.set(action, name);
      if (prev !== undefined) {
        v.push({
          field: `autonomy.${name}`,
          message: `${action} is already listed under ${prev}; an action can be in only one list`,
        });
      }
    }
  }
  if (LEVEL_RANK[a.level] < LEVEL_RANK.L2) {
    const act = a.may.find((x) => ORDER_ACTIONS.includes(x));
    if (act !== undefined) {
      v.push({
        field: 'autonomy.may',
        message: `${act} needs autonomy level L2 or higher; at ${a.level} the agent may only suggest`,
      });
    }
  }

  // --- reporting
  if (m.reporting.alert_channels.length === 0) {
    v.push({
      field: 'reporting.alert_channels',
      message: 'needs at least one channel so a halt reaches a person',
    });
  }
  return v;
}

// ---------------------------------------------------------------------------
// validate_json: structural parse (serde, deny_unknown_fields) then validate
// ---------------------------------------------------------------------------

class ParseError extends Error {}

type Obj = Record<string, unknown>;

function fail(path: string, why: string): never {
  throw new ParseError(`${path}: ${why}`);
}

function asObject(v: unknown, path: string, allowed: readonly string[], required: readonly string[]): Obj {
  if (typeof v !== 'object' || v === null || Array.isArray(v)) fail(path, 'expected an object');
  const o = v as Obj;
  for (const k of Object.keys(o)) {
    if (!allowed.includes(k)) fail(path, `unknown field \`${k}\``);
  }
  for (const k of required) {
    if (!(k in o)) fail(path, `missing field \`${k}\``);
  }
  return o;
}

function asString(v: unknown, path: string): string {
  if (typeof v !== 'string') fail(path, 'expected a string');
  return v;
}

function asNumber(v: unknown, path: string): number {
  if (typeof v !== 'number' || !Number.isFinite(v)) fail(path, 'expected a number');
  return v;
}

function asBool(v: unknown, path: string): boolean {
  if (typeof v !== 'boolean') fail(path, 'expected a boolean');
  return v;
}

function asU32(v: unknown, path: string): number {
  if (typeof v !== 'number' || !Number.isInteger(v) || v < 0 || v > 4294967295) {
    fail(path, 'expected a non-negative whole number');
  }
  return v;
}

function asEnum<T extends string>(v: unknown, path: string, values: readonly string[]): T {
  if (typeof v !== 'string' || !values.includes(v)) {
    fail(path, `expected one of ${values.join(', ')}`);
  }
  return v as T;
}

function asArray<T>(v: unknown, path: string, item: (x: unknown, p: string) => T): T[] {
  if (!Array.isArray(v)) fail(path, 'expected a list');
  return v.map((x, i) => item(x, `${path}[${i}]`));
}

const RFC3339_RE =
  /^(\d{4})-(\d{2})-(\d{2})[Tt ](\d{2}):(\d{2}):(\d{2})(?:\.\d+)?(?:[Zz]|[+-]\d{2}:?\d{2})$/;

function asTimestamp(v: unknown, path: string): string {
  const s = asString(v, path);
  const m = RFC3339_RE.exec(s);
  if (!m) fail(path, 'expected an RFC 3339 timestamp');
  const [y = 0, mo = 0, d = 0, h = 0, mi = 0, se = 0] = m.slice(1).map(Number);
  const daysInMonth = new Date(Date.UTC(y, mo, 0)).getUTCDate();
  if (mo < 1 || mo > 12 || d < 1 || d > daysInMonth || h > 23 || mi > 59 || se > 60) {
    fail(path, 'not a real date and time');
  }
  return s;
}

function parseMoney(v: unknown, path: string): Money {
  const o = asObject(v, path, ['amount', 'ccy'], ['amount', 'ccy']);
  return { amount: asString(o.amount, `${path}.amount`), ccy: asString(o.ccy, `${path}.ccy`) };
}

/**
 * Structural parse of untrusted JSON into a MandateBody, mirroring serde with
 * `deny_unknown_fields`: unknown or missing fields, wrong types, unknown enum
 * values and malformed timestamps are all errors. Optional fields default the
 * way the Rust struct does (`instrument_deny` and `max_asset_class` to empty,
 * a rung's `scale` to none).
 */
function parseMandateBody(value: unknown): MandateBody {
  const root = asObject(
    value,
    'mandate',
    ['basis', 'capital', 'universe', 'exposure', 'loss', 'autonomy', 'reporting'],
    ['basis', 'capital', 'universe', 'exposure', 'loss', 'autonomy', 'reporting'],
  );

  const basis = asObject(root.basis, 'basis', ['capital_source', 'jurisdiction', 'acknowledgements'], [
    'capital_source',
    'jurisdiction',
    'acknowledgements',
  ]);
  const capital = asObject(root.capital, 'capital', ['allocated', 'min_cash_reserve'], [
    'allocated',
    'min_cash_reserve',
  ]);
  const universe = asObject(
    root.universe,
    'universe',
    ['venues', 'asset_classes', 'instrument_allow', 'instrument_deny', 'shorting', 'derivatives', 'leverage_max_gross'],
    ['venues', 'asset_classes', 'instrument_allow', 'shorting', 'derivatives', 'leverage_max_gross'],
  );
  const exposure = asObject(
    root.exposure,
    'exposure',
    ['max_position', 'max_asset_class', 'max_gross', 'max_net', 'max_order_notional', 'max_orders_per_day', 'max_turnover_per_day'],
    ['max_position', 'max_gross', 'max_net', 'max_order_notional', 'max_orders_per_day', 'max_turnover_per_day'],
  );
  const loss = asObject(root.loss, 'loss', ['daily_loss_limit', 'drawdown_ladder', 'resume_after_halt'], [
    'daily_loss_limit',
    'drawdown_ladder',
    'resume_after_halt',
  ]);
  const autonomy = asObject(root.autonomy, 'autonomy', ['level', 'may', 'needs_confirmation', 'never'], [
    'level',
    'may',
    'needs_confirmation',
    'never',
  ]);
  const reporting = asObject(root.reporting, 'reporting', ['digest', 'alert_channels'], [
    'digest',
    'alert_channels',
  ]);

  const strings = (x: unknown, p: string) => asString(x, p);
  const actions = (x: unknown, p: string) => asEnum<AgentAction>(x, p, ALL_ACTIONS);

  let maxAssetClass: Record<string, number> = {};
  if ('max_asset_class' in exposure) {
    const mac = exposure.max_asset_class;
    if (typeof mac !== 'object' || mac === null || Array.isArray(mac)) {
      fail('exposure.max_asset_class', 'expected an object');
    }
    maxAssetClass = {};
    for (const [k, x] of Object.entries(mac as Obj)) {
      maxAssetClass[k] = asNumber(x, `exposure.max_asset_class.${k}`);
    }
  }

  return {
    basis: {
      capital_source: asEnum(basis.capital_source, 'basis.capital_source', ['own', 'third_party', 'mixed']),
      jurisdiction: asString(basis.jurisdiction, 'basis.jurisdiction'),
      acknowledgements: asArray(basis.acknowledgements, 'basis.acknowledgements', (x, p) => {
        const o = asObject(x, p, ['doc', 'doc_version', 'at', 'by'], ['doc', 'doc_version', 'at', 'by']);
        return {
          doc: asString(o.doc, `${p}.doc`),
          doc_version: asString(o.doc_version, `${p}.doc_version`),
          at: asTimestamp(o.at, `${p}.at`),
          by: asString(o.by, `${p}.by`),
        };
      }),
    },
    capital: {
      allocated: parseMoney(capital.allocated, 'capital.allocated'),
      min_cash_reserve: asNumber(capital.min_cash_reserve, 'capital.min_cash_reserve'),
    },
    universe: {
      venues: asArray(universe.venues, 'universe.venues', strings),
      asset_classes: asArray(universe.asset_classes, 'universe.asset_classes', strings),
      instrument_allow: asArray(universe.instrument_allow, 'universe.instrument_allow', strings),
      instrument_deny:
        'instrument_deny' in universe
          ? asArray(universe.instrument_deny, 'universe.instrument_deny', strings)
          : [],
      shorting: asBool(universe.shorting, 'universe.shorting'),
      derivatives: asBool(universe.derivatives, 'universe.derivatives'),
      leverage_max_gross: asNumber(universe.leverage_max_gross, 'universe.leverage_max_gross'),
    },
    exposure: {
      max_position: asNumber(exposure.max_position, 'exposure.max_position'),
      max_asset_class: maxAssetClass,
      max_gross: asNumber(exposure.max_gross, 'exposure.max_gross'),
      max_net: asNumber(exposure.max_net, 'exposure.max_net'),
      max_order_notional: parseMoney(exposure.max_order_notional, 'exposure.max_order_notional'),
      max_orders_per_day: asU32(exposure.max_orders_per_day, 'exposure.max_orders_per_day'),
      max_turnover_per_day: asNumber(exposure.max_turnover_per_day, 'exposure.max_turnover_per_day'),
    },
    loss: {
      daily_loss_limit: asNumber(loss.daily_loss_limit, 'loss.daily_loss_limit'),
      drawdown_ladder: asArray(loss.drawdown_ladder, 'loss.drawdown_ladder', (x, p) => {
        const o = asObject(x, p, ['at', 'action', 'scale'], ['at', 'action']);
        const rung: MandateBody['loss']['drawdown_ladder'][number] = {
          at: asNumber(o.at, `${p}.at`),
          action: asEnum(o.action, `${p}.action`, ['shrink', 'halt_flatten']),
        };
        if ('scale' in o && o.scale !== null) rung.scale = asNumber(o.scale, `${p}.scale`);
        return rung;
      }),
      resume_after_halt: asEnum(loss.resume_after_halt, 'loss.resume_after_halt', ['human_only']),
    },
    autonomy: {
      level: asEnum<AutonomyLevel>(autonomy.level, 'autonomy.level', LEVELS),
      may: asArray(autonomy.may, 'autonomy.may', actions),
      needs_confirmation: asArray(autonomy.needs_confirmation, 'autonomy.needs_confirmation', actions),
      never: asArray(autonomy.never, 'autonomy.never', actions),
    },
    reporting: {
      digest: asEnum(reporting.digest, 'reporting.digest', ['daily', 'weekly']),
      alert_channels: asArray(reporting.alert_channels, 'reporting.alert_channels', strings),
    },
  };
}

export type ParsedMandate =
  | { ok: true; body: MandateBody; violations: [] }
  | { ok: false; violations: Violation[] };

/**
 * Parse untrusted JSON and validate it. A parse failure (missing or unknown
 * field, wrong type) is returned as a single violation on the field `mandate`,
 * exactly like the Rust `validate_json`.
 */
export function validateMandateJson(value: unknown): ParsedMandate {
  let body: MandateBody;
  try {
    body = parseMandateBody(value);
  } catch (err) {
    if (err instanceof ParseError) {
      return { ok: false, violations: [{ field: 'mandate', message: `could not be read: ${err.message}` }] };
    }
    throw err;
  }
  const violations = validateMandate(body);
  return violations.length === 0 ? { ok: true, body, violations: [] } : { ok: false, violations };
}

/** Sorted, de-duplicated field names of a violation list (what the golden vectors compare). */
export function violationFields(violations: Violation[]): string[] {
  return [...new Set(violations.map((x) => x.field))].sort();
}
